import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Wrapper from "../../components/wrapper/Wrapper";
import Heading from "../../components/text/Heading";
import Button from "../../components/button/Button";
import QuestionnaireAPI from "../../API/QuestionnaireAPI/QuestionnaireAPI";
import SimplePagination from "../../components/Pagination/SimplePagination";

const UserQuestions = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const userName = searchParams.get("name") || "User";

  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const fetchUserResponses = useCallback(async () => {
    try {
      setLoading(true);
      // Use admin API to get user responses
      const response = await QuestionnaireAPI.getAllQuestionnaireResponses({
        userId: userId,
        page: currentPage,
        limit: limit,
      });

      if (response.data && response.data.responses) {
        setResponses(response.data.responses);
        setTotalItems(response.data.paginationInfo.totalItems);
        setTotalPages(response.data.paginationInfo.totalPages);
      } else {
        setResponses([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching user responses:", error);
      setError("Failed to fetch user responses");
    } finally {
      setLoading(false);
    }
  }, [userId, currentPage, limit]);

  useEffect(() => {
    if (userId) {
      fetchUserResponses();
    }
  }, [userId, currentPage, limit, fetchUserResponses]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case "text":
        return "Text Input";
      case "single_choice":
        return "Single Choice";
      case "multiple_choice":
        return "Multiple Choice";
      default:
        return type;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Wrapper>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <div className="text-center py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{error}</p>
            <Button
              text="Retry"
              onClick={fetchUserResponses}
              customClass="mt-2 bg-red-600 hover:bg-red-700"
            />
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Heading heading={`${userName}'s Answered Questions`} />
              <p className="mt-2 text-sm text-gray-600">
                View all questionnaire responses from this user
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <Button
                text="Back to Users"
                onClick={() => window.close()}
                customClass="bg-gray-500 hover:bg-gray-600"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-0">
          {responses.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Responses Found
              </h3>
              <p className="text-gray-500">
                This user hasn't answered any questionnaires yet.
              </p>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg">
              {/* Responses Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Question
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User's Answer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Answered On
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {responses
                      .slice((currentPage - 1) * limit, currentPage * limit)
                      .map((response, index) => (
                        <tr
                          key={response._id || index}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <p className="text-sm font-medium text-gray-900">
                                {response.question?.question ||
                                  "Question not available"}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">
                              {getQuestionTypeLabel(
                                response.question?.questionType
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              {Array.isArray(response.answers) ? (
                                <div className="space-y-1">
                                  {response.answers.map((answer, idx) => (
                                    <p
                                      key={idx}
                                      className="text-sm text-gray-900 bg-gray-100 px-2 py-1 rounded"
                                    >
                                      {answer}
                                    </p>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-900 bg-gray-100 px-2 py-1 rounded">
                                  {response.answers || "No answer provided"}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">
                              {formatDate(response.createdAt)}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-3 border-t border-gray-200">
                  <SimplePagination
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    limit={limit}
                    onLimitChange={handleLimitChange}
                    totalItems={totalItems}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default UserQuestions;
